import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoTip } from "@/components/dashboard/InfoTip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronRight, ChevronDown, Check, X, Filter } from "lucide-react";

interface PlatformDetail {
  destination: string;
  source: string;
  has_email: boolean;
  has_phone: boolean;
  has_ip: boolean;
  has_fbp: boolean;
  has_fbc: boolean;
  has_external_id: boolean;
  has_ua: boolean;
  has_fn: boolean;
  has_ln: boolean;
  has_ct: boolean;
  has_st: boolean;
  has_zp: boolean;
  has_country: boolean;
  has_ge: boolean;
  has_ga_client_id: boolean;
  has_ga_session_id: boolean;
  ip_version: string | null;
  fbc_source?: string | null;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  ILS: '₪', GBP: '£', AUD: 'A$', CAD: 'C$', USD: '$', EUR: '€',
};

interface LogEntry {
  created_at: string;
  event_name: string;
  event_id: string;
  channels: string;
  destinations: string[];
  event_value: number | null;
  event_value_usd: number | null;
  currency: string | null;
  original_value: number | null;
  original_currency: string | null;
  order_id: string | null;
  customer_type: string | null;
  country_code: string | null;
  traffic_source: string | null;
  platform_details?: PlatformDetail[];
  dedup_confirmed?: boolean;
  fb_confirmed?: { browser: boolean; server: boolean };
  ga4_confirmed?: { browser: boolean; server: boolean };
  is_vpn?: boolean;
  is_suspicious?: boolean;
  is_abuser?: boolean;
  is_anonymous?: boolean;
  is_bogon?: boolean;
  is_icloud_relay?: boolean;
  asn_type?: string | null;
  asn_name?: string | null;
}

const COUNTRY_FLAGS: Record<string, string> = {
  US: '🇺🇸', IL: '🇮🇱', GB: '🇬🇧', AU: '🇦🇺', CA: '🇨🇦', DE: '🇩🇪', FR: '🇫🇷', JP: '🇯🇵', KR: '🇰🇷', IN: '🇮🇳', HR: '🇭🇷', FI: '🇫🇮', NO: '🇳🇴', ES: '🇪🇸', AE: '🇦🇪',
};

const SOURCE_BADGE_CLASSES: Record<string, string> = {
  facebook_paid: 'bg-blue-100 text-blue-800 border-blue-200',
  facebook_organic: 'bg-blue-50 text-blue-600 border-blue-100',
  google_paid: 'bg-amber-100 text-amber-800 border-amber-200',
  google_organic: 'bg-green-100 text-green-800 border-green-200',
  direct: 'bg-gray-100 text-gray-700 border-gray-200',
  referral: 'bg-purple-100 text-purple-800 border-purple-200',
  unknown: 'bg-gray-50 text-gray-500 border-gray-100',
};

function getSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    facebook_paid: 'FB Paid', facebook_organic: 'FB Organic',
    google_paid: 'Google Paid', google_organic: 'Google Organic',
    direct: 'Direct', referral: 'Referral', unknown: 'Unknown',
  };
  return labels[source] || source;
}

function StatusIcon({ ok }: { ok: boolean }) {
  return ok
    ? <Check className="h-3.5 w-3.5 text-green-600 inline-block" />
    : <X className="h-3.5 w-3.5 text-red-400 inline-block" />;
}

function DedupBadge({ browser, server }: { browser: boolean; server: boolean }) {
  if (browser && server) {
    return <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium"><Check className="h-3 w-3" /> Confirmed</span>;
  }
  if (browser) return <span className="text-amber-600 text-xs font-medium">Browser only</span>;
  if (server) return <span className="text-amber-600 text-xs font-medium">Server only</span>;
  return <span className="text-muted-foreground text-xs">—</span>;
}

function ParamRow({ name, present, annotation }: { name: string; present: boolean; annotation: string }) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <StatusIcon ok={present} />
      <span className="font-mono text-xs w-24">{name}</span>
      <span className="text-xs text-muted-foreground">{present ? annotation : 'Not sent'}</span>
    </div>
  );
}

function ExpandedDetail({ entry }: { entry: LogEntry }) {
  const fb = entry.fb_confirmed || { browser: false, server: false };
  const ga4 = entry.ga4_confirmed || { browser: false, server: false };
  const hasFb = fb.browser || fb.server;
  const hasGa4 = ga4.browser || ga4.server;

  // Find CAPI detail row for FB params
  const capiDetail = entry.platform_details?.find(d => d.destination === 'facebook_capi');
  // Find MP detail row for GA4 params
  const mpDetail = entry.platform_details?.find(d => d.destination === 'ga4_mp');

  return (
    <div className="p-4 bg-muted/20 rounded-lg mx-2 mb-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Facebook */}
        {hasFb && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500" /> Facebook
            </h4>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-28">Browser (Pixel):</span>
                {fb.browser ? <span className="text-green-700 font-medium">Sent</span> : <span className="text-muted-foreground">—</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-28">Server (CAPI):</span>
                {fb.server ? <span className="text-green-700 font-medium">Sent</span> : <span className="text-muted-foreground">—</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-28">Dedup:</span>
                <DedupBadge browser={fb.browser} server={fb.server} />
              </div>
            </div>

            {capiDetail && (
              <div className="mt-3 pt-2 border-t border-border/50">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Parameters sent to CAPI:</p>
                <div className="space-y-0.5">
                  <ParamRow name="email" present={capiDetail.has_email} annotation="SHA-256 hashed" />
                  <ParamRow name="phone" present={capiDetail.has_phone} annotation="SHA-256 hashed" />
                  <ParamRow name="external_id" present={capiDetail.has_external_id} annotation="SHA-256 hashed" />
                  <ParamRow name="ip" present={capiDetail.has_ip} annotation={`Raw (${capiDetail.ip_version || 'IPv4'})`} />
                  <ParamRow name="fbp" present={capiDetail.has_fbp} annotation="Raw cookie value" />
                  <ParamRow name="fbc" present={capiDetail.has_fbc} annotation={capiDetail.has_fbc ? `Raw click ID (${capiDetail.fbc_source || 'unknown'})` : 'Not sent'} />
                  <ParamRow name="user_agent" present={capiDetail.has_ua} annotation="Raw browser string" />
                  <ParamRow name="first_name" present={capiDetail.has_fn} annotation="SHA-256 hashed" />
                  <ParamRow name="last_name" present={capiDetail.has_ln} annotation="SHA-256 hashed" />
                  <ParamRow name="city" present={capiDetail.has_ct} annotation="SHA-256 hashed" />
                  <ParamRow name="state" present={capiDetail.has_st} annotation="SHA-256 hashed" />
                  <ParamRow name="zip" present={capiDetail.has_zp} annotation="SHA-256 hashed" />
                  <ParamRow name="country" present={capiDetail.has_country} annotation="SHA-256 hashed" />
                  <ParamRow name="gender" present={capiDetail.has_ge} annotation="SHA-256 hashed" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Google Analytics */}
        {hasGa4 && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" /> Google Analytics
            </h4>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-28">Browser (gtag):</span>
                {ga4.browser ? <span className="text-green-700 font-medium">Sent</span> : <span className="text-muted-foreground">—</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-28">Server (MP):</span>
                {ga4.server ? <span className="text-green-700 font-medium">Sent</span> : <span className="text-muted-foreground">—</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-28">Dedup:</span>
                <DedupBadge browser={ga4.browser} server={ga4.server} />
              </div>
            </div>

            {mpDetail && (
              <div className="mt-3 pt-2 border-t border-border/50">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Parameters sent to MP:</p>
                <div className="space-y-0.5">
                  <ParamRow name="client_id" present={mpDetail.has_ga_client_id} annotation="GA4 cookie value" />
                  <ParamRow name="session_id" present={mpDetail.has_ga_session_id} annotation="GA4 session value" />
                </div>
              </div>
            )}
          </div>
        )}

        {!hasFb && !hasGa4 && (
          <p className="text-xs text-muted-foreground col-span-2">No platform details available.</p>
        )}
      </div>

      {/* Network / Privacy Info */}
      {(entry.asn_type || entry.asn_name || entry.is_vpn || entry.is_icloud_relay || entry.is_abuser || entry.is_anonymous || entry.is_bogon) && (
        <div className="mt-4 pt-3 border-t border-border/50">
          <p className="text-xs text-muted-foreground mb-2 font-medium">Network & Privacy</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
            {entry.asn_type && (
              <span className="text-muted-foreground">Network: <span className="text-foreground font-medium capitalize">{entry.asn_type}</span></span>
            )}
            {entry.asn_name && (
              <span className="text-muted-foreground">ISP: <span className="text-foreground font-medium">{entry.asn_name}</span></span>
            )}
            {entry.is_vpn && <span className="text-orange-700 font-medium">🛡 VPN</span>}
            {entry.is_icloud_relay && <span className="text-sky-700 font-medium">🍎 iCloud Relay</span>}
            {entry.is_anonymous && <span className="text-amber-700 font-medium">👤 Anonymous</span>}
            {entry.is_abuser && <span className="text-red-700 font-medium">⚠ Abusive IP</span>}
            {entry.is_bogon && <span className="text-gray-600 font-medium">🚫 Bogon</span>}
          </div>
        </div>
      )}
    </div>
  );
}

interface EventLogSectionProps {
  data: { recent_log: LogEntry[] };
  fmtVal: (v: number) => string;
}

export function EventLogSection({ data, fmtVal }: EventLogSectionProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');

  const uniqueEvents = useMemo(() => {
    const set = new Set(data.recent_log.map(e => e.event_name));
    return Array.from(set).sort();
  }, [data.recent_log]);

  const uniqueCountries = useMemo(() => {
    const set = new Set(data.recent_log.map(e => e.country_code).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [data.recent_log]);

  const filteredLog = useMemo(() => {
    return data.recent_log.filter(entry => {
      if (eventFilter !== 'all' && entry.event_name !== eventFilter) return false;
      if (countryFilter !== 'all' && entry.country_code !== countryFilter) return false;
      return true;
    });
  }, [data.recent_log, eventFilter, countryFilter]);

  const toggleRow = (idx: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
           <div className="flex items-center gap-1.5">
              <CardTitle className="text-base">Recent Event Log (last 100)</CardTitle>
              <InfoTip text="Deduplicated log of recent user actions. Each row represents one unique action with all platform destinations shown as badges. Click to expand for full payload details." />
            </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <select
                value={eventFilter}
                onChange={e => { setEventFilter(e.target.value); setExpandedRows(new Set()); }}
                className="text-xs border border-border rounded-md px-2 py-1 bg-background text-foreground"
              >
                <option value="all">All Events</option>
                {uniqueEvents.map(ev => <option key={ev} value={ev}>{ev}</option>)}
              </select>
            </div>
            <select
              value={countryFilter}
              onChange={e => { setCountryFilter(e.target.value); setExpandedRows(new Set()); }}
              className="text-xs border border-border rounded-md px-2 py-1 bg-background text-foreground"
            >
              <option value="all">All Countries</option>
              {uniqueCountries.map(cc => <option key={cc} value={cc}>{(COUNTRY_FLAGS[cc] || '')} {cc}</option>)}
            </select>
            {(eventFilter !== 'all' || countryFilter !== 'all') && (
              <button
                onClick={() => { setEventFilter('all'); setCountryFilter('all'); setExpandedRows(new Set()); }}
                className="text-[10px] text-muted-foreground hover:text-foreground underline"
              >
                Clear
              </button>
            )}
            <span className="text-[10px] text-muted-foreground">{filteredLog.length} / {data.recent_log.length}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead className="text-left">Time</TableHead>
                <TableHead className="text-left">Event</TableHead>
                <TableHead className="text-left">Traffic</TableHead>
                <TableHead className="text-left">Channels</TableHead>
                <TableHead className="text-left">Country</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead className="text-left">Order</TableHead>
                <TableHead className="text-left">Type</TableHead>
                <TableHead className="text-left">Event ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLog.map((entry, i) => {
                const isExpanded = expandedRows.has(i);
                return (
                  <>
                    <TableRow
                      key={`row-${i}`}
                      className={`cursor-pointer hover:bg-muted/50 ${i % 2 === 0 ? 'bg-muted/30' : ''}`}
                      onClick={() => toggleRow(i)}
                    >
                      <TableCell className="w-8 px-2">
                        {isExpanded
                          ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </TableCell>
                      <TableCell className="text-left text-xs whitespace-nowrap font-mono">
                        {new Date(entry.created_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </TableCell>
                      <TableCell className="text-left font-medium">{entry.event_name}</TableCell>
                      <TableCell className="text-left">
                        {entry.traffic_source ? (
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${SOURCE_BADGE_CLASSES[entry.traffic_source] || SOURCE_BADGE_CLASSES.unknown}`}>
                            {getSourceLabel(entry.traffic_source)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-left">
                        <div className="flex flex-wrap gap-1">
                          {entry.destinations.includes('facebook_pixel') && (
                            <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 border border-blue-200 px-1.5 py-0.5 text-[10px] font-semibold">FB</span>
                          )}
                          {entry.destinations.includes('facebook_capi') && (
                            <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-600 border border-blue-100 px-1.5 py-0.5 text-[10px] font-semibold">CAPI</span>
                          )}
                          {entry.destinations.includes('ga4_gtag') && (
                            <span className="inline-flex items-center rounded-full bg-green-100 text-green-800 border border-green-200 px-1.5 py-0.5 text-[10px] font-semibold">GA4</span>
                          )}
                          {entry.destinations.includes('ga4_mp') && (
                            <span className="inline-flex items-center rounded-full bg-green-50 text-green-600 border border-green-100 px-1.5 py-0.5 text-[10px] font-semibold">MP</span>
                          )}
                          {entry.dedup_confirmed && (
                            <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold">✓ Dedup</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-left text-xs">
                        {entry.country_code ? (
                          <span title={entry.country_code}>
                            {COUNTRY_FLAGS[entry.country_code] || ''} {entry.country_code}
                          </span>
                        ) : '—'}
                        {entry.is_icloud_relay && (
                          <span className="ml-1 inline-flex items-center rounded-full bg-sky-100 text-sky-800 border border-sky-200 px-1 py-0 text-[9px] font-semibold" title="Apple iCloud Private Relay">iCloud</span>
                        )}
                        {entry.is_vpn && (
                          <span className="ml-1 inline-flex items-center rounded-full bg-orange-100 text-orange-800 border border-orange-200 px-1 py-0 text-[9px] font-semibold" title="VPN/Proxy detected">VPN</span>
                        )}
                        {entry.is_anonymous && !entry.is_vpn && (
                          <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-1 py-0 text-[9px] font-semibold" title="Anonymous traffic">Anon</span>
                        )}
                        {entry.is_abuser && (
                          <span className="ml-1 inline-flex items-center rounded-full bg-red-100 text-red-800 border border-red-200 px-1 py-0 text-[9px] font-semibold" title="Known abusive IP">Abuser</span>
                        )}
                        {entry.is_bogon && (
                          <span className="ml-1 inline-flex items-center rounded-full bg-gray-200 text-gray-700 border border-gray-300 px-1 py-0 text-[9px] font-semibold" title="Bogon IP (invalid)">Bogon</span>
                        )}
                        {entry.is_suspicious && !entry.is_vpn && !entry.is_anonymous && !entry.is_abuser && !entry.is_bogon && (
                          <span className="ml-1 inline-flex items-center rounded-full bg-red-100 text-red-800 border border-red-200 px-1 py-0 text-[9px] font-semibold" title="Suspicious traffic (proxy/tor/hosting)">⚠</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono">
                        {entry.event_value_usd ? (
                          <div>
                            <span>{fmtVal(entry.event_value_usd)}{entry.original_currency ? '*' : ''}</span>
                            {entry.original_currency && entry.original_value && (
                              <div className="text-[10px] text-muted-foreground">
                                ({CURRENCY_SYMBOLS[entry.original_currency] || ''}{Math.round(entry.original_value)} {entry.original_currency})
                              </div>
                            )}
                          </div>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-left text-xs font-mono">{entry.order_id || '—'}</TableCell>
                      <TableCell className="text-left text-xs">{entry.customer_type || '—'}</TableCell>
                      <TableCell className="text-left text-xs font-mono text-muted-foreground" title={entry.event_id || ''}>
                        {entry.event_id ? entry.event_id.slice(0, 8) + '…' : '—'}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow key={`detail-${i}`}>
                        <TableCell colSpan={10} className="p-0 border-0">
                          <ExpandedDetail entry={entry} />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
              {filteredLog.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground">
                  {data.recent_log.length === 0 ? 'No events yet' : 'No matching events'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
